import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck, Sparkles, Zap, MapPin, Plus, KeyRound, Car as CarIcon, Tag, ChevronRight } from "lucide-react";
import api from "../lib/api";
import CarCard from "../components/CarCard";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/I18nContext";

export default function Home() {
  const [tab, setTab] = useState("buy");
  const [search, setSearch] = useState("");
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [brands, setBrands] = useState([]);
  const [dealers, setDealers] = useState([]);
  const navigate = useNavigate();
  const { t } = useI18n();

  useEffect(() => {
    Promise.all([
      api.get("/listings", { params: { featured: true, listing_type: "sale", limit: 6 } }),
      api.get("/listings", { params: { listing_type: "sale", sort: "newest", limit: 8 } }),
      api.get("/listings", { params: { listing_type: "rental", limit: 6 } }),
      api.get("/brands"),
      api.get("/dealers", { params: { verified: true } }),
    ]).then(([f, l, r, b, d]) => {
      setFeatured(f.data.items || []);
      setLatest(l.data.items || []);
      setRentals(r.data.items || []);
      setBrands(b.data || []);
      setDealers(d.data || []);
    });
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const path = tab === "rent" ? "/rent" : "/buy";
    navigate(`${path}?q=${encodeURIComponent(search)}`);
  };

  return (
    <div data-testid="home-page">
      <Seo
        title={null}
        description={t("home.hero.subtitle")}
        image="https://customer-assets.emergentagent.com/job_carmarket-preview/artifacts/vmguiuiy_logo%20v9%20use-02%282%29.png"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-cm-primary text-white">
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(230,57,70,0.4), transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1), transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
          <div className="max-w-2xl fade-in-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-medium tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" /> {t("home.hero.badge")}
            </span>
            <h1 className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              {t("home.hero.title_1")} <span className="text-cm-accent">{t("home.hero.title_2")}</span>
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-xl">
              {t("home.hero.subtitle")}
            </p>
          </div>

          {/* Search card */}
          <div className="mt-8 md:mt-10 max-w-3xl bg-white text-cm-primary rounded-2xl shadow-2xl p-2 fade-in-up">
            <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-2">
              {[
                { id: "buy", label: t("home.hero.tab.buy") },
                { id: "rent", label: t("home.hero.tab.rent") },
                { id: "sell", label: t("home.hero.tab.sell") },
              ].map(tt => (
                <button
                  key={tt.id}
                  data-testid={`hero-tab-${tt.id}`}
                  onClick={() => tt.id === "sell" ? navigate("/sell") : setTab(tt.id)}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === tt.id ? 'bg-white shadow-sm text-cm-primary' : 'text-slate-600 hover:text-cm-primary'}`}
                >{tt.label}</button>
              ))}
            </div>
            <form onSubmit={submitSearch} className="flex items-center gap-2 p-2">
              <Search className="w-5 h-5 text-slate-400 ml-2" />
              <input
                data-testid="hero-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("home.hero.search_placeholder")}
                className="flex-1 bg-transparent outline-none text-base placeholder:text-slate-400 py-2"
              />
              <button
                type="submit"
                data-testid="hero-search-submit"
                className="bg-cm-accent text-white px-5 py-2.5 rounded-xl font-medium hover:bg-cm-accent-hover transition-colors press-scale flex items-center gap-2"
              >
                {t("home.hero.search_btn")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Quick actions */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            {[
              { to: "/buy", icon: CarIcon, label: t("home.qa.buy"), testid: "qa-buy" },
              { to: "/rent", icon: KeyRound, label: t("home.qa.rent"), testid: "qa-rent" },
              { to: "/sell", icon: Plus, label: t("home.qa.sell"), testid: "qa-sell" },
              { to: "/login", icon: Tag, label: t("home.qa.dealer"), testid: "qa-dealer" },
            ].map((q) => (
              <Link key={q.to} to={q.to} data-testid={q.testid}
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 border border-white/20 hover:bg-white/20 transition-colors press-scale">
                <q.icon className="w-5 h-5 text-cm-accent" />
                <span className="text-sm font-medium">{q.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured cars */}
      <Section title={t("home.featured")} subtitle={t("home.featured.sub")} link="/buy" linkLabel={t("home.view_all")} testid="featured-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {featured.slice(0, 6).map((l) => <CarCard key={l.id} listing={l} />)}
        </div>
      </Section>

      {/* Popular brands */}
      <Section title={t("home.brands")} testid="brands-section">
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {brands.map((b) => (
            <Link key={b.id} to={`/buy?brand=${encodeURIComponent(b.name)}`} data-testid={`brand-${b.name}`}
              className="flex-shrink-0 w-28 h-28 rounded-2xl border border-slate-200 bg-white hover:shadow-md transition-all flex flex-col items-center justify-center gap-2 press-scale">
              <div className="w-14 h-14 flex items-center justify-center">
                <img src={b.logo} alt={b.name} className="max-w-full max-h-full object-contain" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <span className="text-xs font-medium text-cm-primary text-center px-2">{b.name}</span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Latest listings */}
      <Section title={t("home.latest")} subtitle={t("home.latest.sub")} link="/buy" linkLabel={t("home.view_all")} testid="latest-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 stagger">
          {latest.slice(0, 8).map((l) => <CarCard key={l.id} listing={l} compact />)}
        </div>
      </Section>

      {/* Rentals */}
      <Section title={t("home.rentals")} subtitle={t("home.rentals.sub")} link="/rent" linkLabel={t("home.view_all")} testid="rentals-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
          {rentals.slice(0, 3).map((l) => <CarCard key={l.id} listing={l} />)}
        </div>
      </Section>

      {/* Trusted dealers */}
      <Section title={t("home.dealers")} subtitle={t("home.dealers.sub")} testid="dealers-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {dealers.map((d) => (
            <Link key={d.id} to={`/dealer/${d.id}`} data-testid={`dealer-card-${d.id}`}
              className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all flex items-center gap-4 press-scale">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 overflow-hidden flex-shrink-0">
                {d.logo ? <img src={d.logo} alt={d.dealer_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-cm-primary font-display font-bold">{d.dealer_name?.[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <h4 className="font-semibold text-cm-primary truncate">{d.dealer_name}</h4>
                  {d.verified_status && <ShieldCheck className="w-4 h-4 text-emerald-500" />}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                  <MapPin className="w-3 h-3" /> <span className="truncate">{d.address}</span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <Section title={t("home.how")} testid="how-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: Search, title: t("home.how.browse.t"), desc: t("home.how.browse.d") },
            { icon: Zap, title: t("home.how.connect.t"), desc: t("home.how.connect.d") },
            { icon: ShieldCheck, title: t("home.how.confidence.t"), desc: t("home.how.confidence.d") },
          ].map((s) => (
            <div key={s.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
              <div className="w-12 h-12 rounded-xl bg-cm-primary text-white flex items-center justify-center mb-4">
                <s.icon className="w-6 h-6" />
              </div>
              <h4 className="font-display font-bold text-lg text-cm-primary">{s.title}</h4>
              <p className="text-sm text-slate-600 mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, subtitle, link, linkLabel = "View all", children, testid }) {
  return (
    <section data-testid={testid} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cm-primary">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {link && (
          <Link to={link} className="text-sm font-medium text-cm-accent hover:underline flex items-center gap-1">
            {linkLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}
